import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BubbleChat } from 'flowise-embed-react';
import styles from './RtcAiFrame.module.css';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  sources?: {
    filepath: string;
    bulletin_date: string;
    page_number: number;
    thumbnail_url: string | null;
    pdf_url: string;
  }[];
}

const AVAILABLE_MODELS = [
  { id: 'llama3.3:70b-instruct-q4_0', name: 'Llama 3.3 (70B)' },
];

const BASE_URL = 'https://slack.westchesterrtc.com';

const TypingIndicator = () => (
  <div className={styles.typing}>
    Thinking
    <span className={styles.dot}>.</span>
    <span className={styles.dot}>.</span>
    <span className={styles.dot}>.</span>
  </div>
);

const RtcAiFrame = () => {
  const navigate = useNavigate();
  const { user, hasAccess } = useAuth();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: 'Hello! I am CINDI (Centralized Intelligence Network for Data Integration). Please provide context in the fields above to help me find the bulletins you need. You can also ask me questions about any bulletins that match the listed context by typing in the text box above.',
  }]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [formFields, setFormFields] = useState({
    first_name: '',
    last_name: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    license_plate: '',
    jurisdiction: '',
    crime_type: '',
    misc_keyword: '',
    time_from: '2000-01-01',
    time_to: new Date().toISOString().split('T')[0]
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    if (!hasAccess('insight')) {
      navigate('/');
      return;
    }
  }, [user, hasAccess, navigate]);

  useEffect(() => {
    // Only auto-scroll if we're not loading (AI is not typing)
    if (!loading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormFields({ ...formFields, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const query = input.trim() || 'Please describe the bulletins that match the listed context';
    const userMessage = { role: 'user' as const, content: query };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Only scroll to bottom after user message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    try {
      // Log the search query and context
      try {
        await fetch('/insight/api/admin/log-access', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            resource: 'rtc_ai_search',
            action: `Searched with query: "${query}" and context: ${JSON.stringify(formFields)}`
          }),
          credentials: 'include'
        });
      } catch (err) {
        console.error('Failed to log search:', err);
        // Continue even if logging fails
      }

      const response = await fetch('/rag/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ query, ...formFields }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      let assistantMessage = '';
      const sourcesHeader = response.headers.get('x-documents-used');
      const sources = sourcesHeader ? JSON.parse(sourcesHeader) : undefined;

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let lines = buffer.split('\n');

          buffer = lines.pop()!;

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'content') {
                  assistantMessage += data.content;
                  setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage.role === 'assistant') {
                      return [
                        ...prev.slice(0, -1),
                        { ...lastMessage, content: assistantMessage, sources }
                      ];
                    } else {
                      return [
                        ...prev,
                        { role: 'assistant', content: assistantMessage, sources }
                      ];
                    }
                  });
                }
              } catch (e) {
                console.error('Error parsing SSE message:', e);
              }
            }
          }
        }
      }
    } catch (err) {
      setError(`Failed to get a response: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setTimeout(() => setError(''), 10000);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearConversation = () => {
    setMessages([
      { role: 'assistant', content: 'Hello! I am CINDI. How can I assist you with your investigation today?' },
    ]);
    setFormFields({
      first_name: '',
      last_name: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_color: '',
      license_plate: '',
      jurisdiction: '',
      crime_type: '',
      misc_keyword: '',
      time_from: '2000-01-01',
      time_to: new Date().toISOString().split('T')[0]
    });
  };

  if (!user) {
    return <div className={styles.loading}>Checking permissions...</div>;
  }

  return (
    <div className={styles.fullPageWrapper} data-theme="dark">
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.header}>
            <button onClick={() => navigate('/')} className={styles.backButton}>← Back</button>
            <h1>CINDI</h1>
            <button onClick={clearConversation} className={styles.clearButton}>Clear</button>
          </div>

          <form onSubmit={handleSubmit} className={styles.inputForm}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Context Fields</h3>
              <div className={styles.contextFields}>
                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>Incident</h4>
                  <div className={styles.subsectionFields}>
                    <div className={styles.formGroup}>
                      <label htmlFor="jurisdiction">Jurisdiction</label>
                      <input 
                        id="jurisdiction"
                        name="jurisdiction" 
                        placeholder="Enter jurisdiction" 
                        onChange={handleFormChange} 
                        value={formFields.jurisdiction} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="crime_type">Crime Type</label>
                      <input 
                        id="crime_type"
                        name="crime_type" 
                        placeholder="Enter crime type" 
                        onChange={handleFormChange} 
                        value={formFields.crime_type} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="misc_keyword">Misc. Keyword</label>
                      <input 
                        id="misc_keyword"
                        name="misc_keyword" 
                        placeholder="Enter keyword" 
                        onChange={handleFormChange} 
                        value={formFields.misc_keyword} 
                      />
                    </div>
                    <div className={styles.timeFields}>
                      <div className={styles.formGroup}>
                        <label htmlFor="time_from">From</label>
                        <input 
                          id="time_from"
                          name="time_from" 
                          type="date" 
                          onChange={handleFormChange} 
                          value={formFields.time_from} 
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor="time_to">To</label>
                        <input 
                          id="time_to"
                          name="time_to" 
                          type="date" 
                          onChange={handleFormChange} 
                          value={formFields.time_to} 
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>Vehicle</h4>
                  <div className={styles.subsectionFields}>
                    <div className={styles.formGroup}>
                      <label htmlFor="vehicle_make">Make</label>
                      <input 
                        id="vehicle_make"
                        name="vehicle_make" 
                        placeholder="Enter vehicle make" 
                        onChange={handleFormChange} 
                        value={formFields.vehicle_make} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="vehicle_model">Model</label>
                      <input 
                        id="vehicle_model"
                        name="vehicle_model" 
                        placeholder="Enter vehicle model" 
                        onChange={handleFormChange} 
                        value={formFields.vehicle_model} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="vehicle_color">Color</label>
                      <input 
                        id="vehicle_color"
                        name="vehicle_color" 
                        placeholder="Enter vehicle color" 
                        onChange={handleFormChange} 
                        value={formFields.vehicle_color} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="license_plate">License Plate</label>
                      <input 
                        id="license_plate"
                        name="license_plate" 
                        placeholder="Enter license plate" 
                        onChange={handleFormChange} 
                        value={formFields.license_plate} 
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.subsection}>
                  <h4 className={styles.subsectionTitle}>Suspect</h4>
                  <div className={styles.subsectionFields}>
                    <div className={styles.formGroup}>
                      <label htmlFor="first_name">First Name</label>
                      <input 
                        id="first_name"
                        name="first_name" 
                        placeholder="Enter first name" 
                        onChange={handleFormChange} 
                        value={formFields.first_name} 
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="last_name">Last Name</label>
                      <input 
                        id="last_name"
                        name="last_name" 
                        placeholder="Enter last name" 
                        onChange={handleFormChange} 
                        value={formFields.last_name} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.querySection}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Please describe the bulletins that match the listed context"
                  disabled={loading}
                  className={styles.inputField}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className={styles.sendButton}
                >Send</button>
              </div>
            </div>
          </form>

          <div className={styles.chatContainer}>
            <div className={styles.messages}>
              {messages.filter(msg => msg.role !== 'system').map((msg, index) => (
                <div
                  key={index}
                  className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}
                >
                  <span className={styles.avatar}>{msg.role === 'user' ? '👮' : '🤖'}</span>
                  <div className={styles.messageContent}>
                    <div className={styles.markdownContent}>
                      {msg.content.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                    {msg.sources && msg.sources.length > 0 && (
                      <div className={styles.sources}>
                        <h4>Sources:</h4>
                        <div className={styles.sourcesGrid}>
                          {msg.sources.map((source, idx) => (
                            <div key={idx} className={styles.source}>
                              <div className={styles.sourceHeader}>
                                <span className={styles.bulletinDate}>{source.bulletin_date}</span>
                                <span className={styles.pageNumber}>Page {source.page_number}</span>
                              </div>
                              {source.thumbnail_url && (
                                <a 
                                  href={`${BASE_URL}${source.pdf_url}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className={styles.thumbnailLink}
                                >
                                  <img
                                    src={`${BASE_URL}${source.thumbnail_url}`}
                                    alt={`Page ${source.page_number} from ${source.bulletin_date}`}
                                    className={styles.thumbnail}
                                  />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className={`${styles.message} ${styles.assistantMessage}`}>
                  <span className={styles.avatar}>🤖</span>
                  <div className={styles.messageContent}>
                    <TypingIndicator />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {error && <div className={styles.error}>{error}</div>}
          </div>
        </div>
      </div>
      <BubbleChat
        chatflowid="11c36733-552c-4662-8b31-42884d11bb34"
        apiHost="/flowise"
        chatflowConfig={{}}
        observersConfig={{
          observeUserInput: () => {},
          observeLoading: () => {},
          observeMessages: () => {}
        }}
        theme={{    
          button: {
            backgroundColor: '#1a1a1a',
            right: 20,
            bottom: 0,
            size: 80,
            dragAndDrop: true,
            iconColor: 'white',
            customIconSrc: '/images/RTC-blue_logo.png',
            autoWindowOpen: {
              autoOpen: false,
              openDelay: 2,
              autoOpenOnMobile: false
            }
          },
          tooltip: {
            showTooltip: true,
            tooltipMessage: 'Booking Search',
            tooltipBackgroundColor: '#1a1a1a',
            tooltipTextColor: 'white',
            tooltipFontSize: 38
          },
          disclaimer: {
            title: 'Disclaimer',
            message: "By using this chatbot, you agree to the <a target=\"_blank\" href=\"#\">Terms & Conditions</a>",
            textColor: 'white',
            buttonColor: '#3b82f6',
            buttonText: 'Start Chatting',
            buttonTextColor: 'white',
            blurredBackgroundColor: 'rgba(0, 0, 0, 0.8)',
            backgroundColor: '#1a1a1a'
          },
          customCSS: `
            .flowise-chat-window {
              position: fixed !important;
              right: 20px !important;
              bottom: 20px !important;
              width: 600px !important;
              height: 800px !important;
              z-index: 9999 !important;
            }
            .flowise-chat-window iframe {
              width: 100% !important;
              height: 100% !important;
              border: none !important;
            }
            .flowise-chat-message-avatar {
              font-size: 24px !important;
              line-height: 24px !important;
              width: 32px !important;
              height: 32px !important;
            }
            .flowise-chat-button {
              z-index: 10000 !important;
            }
            .flowise-chat-window * {
              cursor: move !important;
            }
            .flowise-chat-window input,
            .flowise-chat-window textarea {
              cursor: text !important;
              background-color: #2a2a2a !important;
              color: white !important;
            }
            .flowise-chat-window button {
              cursor: pointer !important;
            }
          `,
          chatWindow: {
            showTitle: true,
            showAgentMessages: false,
            title: 'Booking Search',
            titleAvatarSrc: '/images/RTC-blue_logo.png',
            welcomeMessage: 'Please enter the first and last name of the subject or specific details about the booking(s) you are looking for',
            errorMessage: 'This is a custom error message',
            backgroundColor: '#1a1a1a',
            backgroundImage: '',
            height: 800,
            width: 1100,
            fontSize: 16,
            starterPrompts: [
            ],
            starterPromptFontSize: 15,
            clearChatOnReload: true,
            sourceDocsTitle: 'Sources:',
            renderHTML: true,
            botMessage: {
              backgroundColor: '#2a2a2a',
              textColor: 'white',
              showAvatar: true,
              avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">🤖</text></svg>'
            },
            userMessage: {
              backgroundColor: '#3B81F6',
              textColor: 'white',
              showAvatar: true,
              avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">👮</text></svg>'
            },
            textInput: {
              placeholder: 'Type your question',
              backgroundColor: '#2a2a2a',
              textColor: 'white',
              sendButtonColor: '#3B81F6',
              maxChars: 150,
              maxCharsWarningMessage: 'You exceeded the characters limit. Please input less than 50 characters.',
              autoFocus: true,
              sendMessageSound: true,
              sendSoundLocation: 'send_message.mp3',
              receiveMessageSound: true,
              receiveSoundLocation: 'receive_message.mp3'
            },
            feedback: {
              color: 'white'
            },
            dateTimeToggle: {
              date: true,
              time: true
            },
            footer: {
              textColor: 'white',
              text: 'Powered',
              company: 'by Cops for Cops',
              companyLink: 'https://slack.westchesterrtc.com'
            }
          }
        }}
      />
      <BubbleChat
        chatflowid="c1001dda-4543-4f69-a285-7d6cc67b9091"
        apiHost="/flowise"
        chatflowConfig={{}}
        observersConfig={{
          observeUserInput: () => {},
          observeLoading: () => {},
          observeMessages: () => {}
        }}
        theme={{    
          button: {
            backgroundColor: '#1a1a1a',
            right: 20,
            bottom: 850,
            size: 80,
            dragAndDrop: true,
            iconColor: 'white',
            customIconSrc: '/images/RTC-blue_logo.png',
            autoWindowOpen: {
              autoOpen: false,
              openDelay: 2,
              autoOpenOnMobile: false
            }
          },
          tooltip: {
            showTooltip: true,
            tooltipMessage: 'Bulletin Search',
            tooltipBackgroundColor: '#1a1a1a',
            tooltipTextColor: 'white',
            tooltipFontSize: 38
          },
          disclaimer: {
            title: 'Disclaimer',
            message: "Information provided may be outdated. Please verify any information prior to taking police action.",
            textColor: 'white',
            buttonColor: '#3b82f6',
            buttonText: 'Start Chatting',
            buttonTextColor: 'white',
            blurredBackgroundColor: 'rgba(0, 0, 0, 0.8)',
            backgroundColor: '#1a1a1a'
          },
          customCSS: `
            .flowise-chat-window {
              position: fixed !important;
              right: 100px !important;
              bottom: 20px !important;
              width: 600px !important;
              height: 800px !important;
              z-index: 9999 !important;
            }
            .flowise-chat-window iframe {
              width: 100% !important;
              height: 100% !important;
              border: none !important;
            }
            .flowise-chat-message-avatar {
              font-size: 24px !important;
              line-height: 24px !important;
              width: 32px !important;
              height: 32px !important;
            }
            .flowise-chat-button {
              z-index: 10000 !important;
            }
            .flowise-chat-window * {
              cursor: move !important;
            }
            .flowise-chat-window input,
            .flowise-chat-window textarea {
              cursor: text !important;
              background-color: #2a2a2a !important;
              color: white !important;
            }
            .flowise-chat-window button {
              cursor: pointer !important;
            }
          `,
          chatWindow: {
            showTitle: true,
            showAgentMessages: false,
            title: 'Bulletin Search',
            titleAvatarSrc: '/images/RTC-blue_logo.png',
            welcomeMessage: 'Hello! Please enter as much information as possible',
            errorMessage: 'This is a custom error message',
            backgroundColor: '#1a1a1a',
            backgroundImage: '',
            height: 800,
            width:1100,
            fontSize: 16,
            starterPrompts: [
              "Any burglary suspects using a white SUV?",
              "Show me missing person bulletins from 2024."
            ],
            starterPromptFontSize: 15,
            clearChatOnReload: true,
            sourceDocsTitle: 'Sources:',
            renderHTML: true,
            botMessage: {
              backgroundColor: '#2a2a2a',
              textColor: 'white',
              showAvatar: true,
              avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">🤖</text></svg>'
            },
            userMessage: {
              backgroundColor: '#3B81F6',
              textColor: 'white',
              showAvatar: true,
              avatarSrc: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="80" font-size="80">👮</text></svg>'
            },
            textInput: {
              placeholder: 'Type your question',
              backgroundColor: '#2a2a2a',
              textColor: 'white',
              sendButtonColor: '#3B81F6',
              maxChars: 50,
              maxCharsWarningMessage: 'You exceeded the characters limit. Please input less than 50 characters.',
              autoFocus: true,
              sendMessageSound: true,
              sendSoundLocation: 'send_message.mp3',
              receiveMessageSound: true,
              receiveSoundLocation: 'receive_message.mp3'
            },
            feedback: {
              color: 'white'
            },
            dateTimeToggle: {
              date: true,
              time: true
            },
            footer: {
              textColor: 'white',
              text: 'Powered',
              company: 'by Cops for Cops',
              companyLink: 'https://slack.westchesterrtc.com'
            }
          }
        }}
      />
    </div>
  );
};

export default RtcAiFrame;